<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <TechnicianPlotsView
        v-if="viewMode === 'technicianPlots'"
        @back="viewMode = 'requests'"
      />
      <template v-else>
      <div class="d-flex mb-8">
        <h2 class="font-weight-bold mb-6">{{ $t('carbonCredit.plotTreeMapping.title') }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class="mr-2" @click="viewMode = 'technicianPlots'">
          {{ $t('carbonCredit.plotTreeMapping.techniciansPlots') }}
        </v-btn>
        <v-btn color="primary" outlined class="mr-2" @click="openExportDataDialog">
          Export Data
        </v-btn>
        <!-- Import data from sheet -->
        <v-btn color="primary" dark class="mr-2" @click="importDataFromSheet">
          {{ $t('carbonCredit.plotTreeMapping.imports.importData') }}
        </v-btn>
        <v-btn color="primary" dark class="mr-2" @click="addNewRequest">
          {{ $t('carbonCredit.plotTreeMapping.addANewRequest') }}
        </v-btn>
      </div>
      <!-- Data Table Card -->
      <v-card elevation="0" class="px-5 mt-7">
        <v-card-text>
          <v-row style="margin-left: -30px; margin-bottom: 20px; margin-top: 15px;">
            <v-col cols="12">
              <v-data-table
                :headers="displayHeaders"
                :items="items"
                :items-per-page="options.limit"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                item-key="id"
                hide-default-footer
                :loading="tableLoading"
                :options.sync="options"
                :server-items-length="totalRequests"
                :loading-text="$t('loadingData')"
              >
                <!-- Table Top Slot: Search, Filters, Pagination, and Actions -->
                <template v-slot:top>
                  <div class="d-flex mt-5">
                    <!-- Search Field -->
                    <div class="ml-3" style="width: 220px;">
                      <label for="text"> </label>
                      <v-text-field
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        :placeholder="$t('carbonCredit.search')"
                        dense
                        v-model="search"
                        @input="searchQuery"
                        class="shrink"
                      ></v-text-field>
                    </div>
                    <!-- Filters -->
                    <div class="ml-3" style="width: 220px;">
                      <v-select
                        v-model="filters.projectId"
                        :items="projectFilterOptions"
                        :label="$t('carbonCredit.plotTreeMapping.project')"
                        item-text="project_title"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        clearable
                        :loading="projectsLoading"
                        @change="onFilterChange"
                      />
                    </div>
                    <div class="ml-3" style="width: 200px;">
                      <v-select
                        v-model="filters.assignedTo"
                        :items="assignedToOptions"
                        label="Assigned To"
                        dense
                        outlined
                        hide-details
                        clearable
                        item-text="fullName"
                        item-value="id"
                        @change="onFilterChange"
                      />
                    </div>
                    <div class="ml-3" style="width: 200px;">
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        label="Status"
                        dense
                        outlined
                        hide-details
                        clearable
                        @change="onFilterChange"
                      />
                    </div>
                    <v-spacer></v-spacer>
                    <!-- Pagination Info -->
                    <div class="py-1 title">
                      {{ fromRequests }} - {{ toRequests }} {{ $t("of") }} {{ totalRequests }}
                    </div>
                    <!-- Pagination Controls -->
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      :disabled="options.page <= 1"
                      @click="pageChange(false)"
                    >
                      <v-icon dark>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      :disabled="options.page >= totalPages"
                      @click="pageChange(true)"
                    >
                      <v-icon dark>mdi-chevron-right</v-icon>
                    </v-btn>
                    <!-- Customization Menu -->
                    <v-menu
                      v-model="menu"
                      :close-on-content-click="false"
                      :nudge-width="200"
                      left
                      :min-width="417"
                    >
                      <template v-slot:activator="props">
                        <v-btn
                          class="mx-2"
                          fab
                          small
                          outlined
                          color="primary"
                          v-bind="props.attrs"
                          v-on="props.on"
                        >
                          <v-icon dark>mdi-cog-outline</v-icon>
                        </v-btn>
                      </template>
                      <v-card width="417">
                        <v-list>
                          <v-list-item>
                            <v-list-item-title>{{ $t("customization") }}</v-list-item-title>
                          </v-list-item>
                          <v-divider></v-divider>
                          <div class="pl-3 pt-2 font-weight-normal">
                            {{ $t("recordsPerPage") }}
                          </div>
                          <v-list-item>
                            <v-slider
                              v-model="itemsPerPageSlider"
                              :tick-labels="itemsPerPageOptions"
                              :max="3"
                              step="1"
                              ticks="always"
                              tick-size="4"
                            ></v-slider>
                          </v-list-item>
                          <v-divider class="mt-n3"></v-divider>
                        </v-list>
                        <v-card-text>
                          <v-container fluid>
                            <v-row>
                              <v-col
                                class="mt-n9"
                                cols="6"
                                v-for="header in headers"
                                :key="header.value"
                              >
                                <v-checkbox
                                  v-if="header.text"
                                  v-model="selectedHeaders"
                                  :label="header.text"
                                  color="primary"
                                  :value="header.text"
                                  hide-details
                                ></v-checkbox>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-layout justify-center>
                          <v-card-actions class="mb-5">
                            <v-spacer></v-spacer>
                            <v-btn outlined color="primary" @click="resetDefault" width="190">
                              {{ $t("restoreDefault") }}
                            </v-btn>
                            <v-btn color="primary" @click="resetTableStructure" width="190">
                              {{ $t("apply") }}
                            </v-btn>
                          </v-card-actions>
                        </v-layout>
                      </v-card>
                    </v-menu>
                    <!-- Download menu (PDF / Excel) and Print Buttons -->
                    <v-menu offset-y left :close-on-content-click="true">
                      <template v-slot:activator="{ on, attrs }">
                        <v-tooltip top color="black" max-width="350">
                          <template v-slot:activator="props">
                            <v-btn
                              class="mx-2"
                              fab
                              small
                              outlined
                              color="primary"
                              v-bind="{ ...props.attrs, ...attrs }"
                              v-on="{ ...props.on, ...on }"
                            >
                              <v-img src="/icons/download.svg"></v-img>
                            </v-btn>
                          </template>
                          <span>{{ $t("Download") }}</span>
                        </v-tooltip>
                      </template>
                      <v-list dense>
                        <v-list-item @click="openDownloadDialog('pdf')">
                          <v-list-item-title>Download as PDF</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openDownloadDialog('xlsx')">
                          <v-list-item-title>Download as XLS</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="props">
                        <v-btn
                          class="mx-2"
                          fab
                          small
                          outlined
                          color="primary"
                          v-bind="props.attrs"
                          v-on="props.on"
                          @click="printPDF"
                        >
                          <v-icon dark>mdi-printer</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Print") }}</span>
                    </v-tooltip>
                  </div>
                </template>
                <!-- Table Body: Custom Rendering of Rows -->
                <template v-if="items.length" v-slot:body="{ items }">
                  <tbody>
                    <tr
                      v-for="item in items"
                      :key="item.id"
                      style="position: relative; width: 0; height: 0"
                      @mouseover="selectItem(item)"
                      @mouseleave="selectItem(false)"
                    >
                      <template v-for="h in displayHeaders">
                        <td
                          class="text-truncate"
                          :key="h.value + '_request_id'"
                          v-if="h.value === 'request_id'"
                        >
                          <span class="font-weight-medium">{{ safeText(item.request_id) }}</span>
                        </td>
                        <td
                          class="text-truncate"
                          :key="h.value + '_project'"
                          v-else-if="h.value === 'project'"
                        >
                          <span>{{ safeText(item.project?.project_title || '—') }}</span>
                        </td>
                        <td
                          class="text-truncate"
                          :key="h.value + '_assignedTo'"
                          v-else-if="h.value === 'assignedTo'"
                        >
                          <div v-if="item.assignees && item.assignees.length > 0">
                            <span
                              v-for="(assignee, index) in item.assignees"
                              :key="index"
                            >
                              {{ safeText(assignee.assignee.fullName || `User ${assignee.assignee.id}`) }}
                              <br v-if="index < item.assignees.length - 1" />
                            </span>
                          </div>
                          <span v-else class="text-grey">Not assigned</span>
                        </td>
                        <td
                          class="text-truncate"
                          :key="h.value + '_numberOfPlots'"
                          v-else-if="h.value === 'numberOfPlots'"
                        >
                          <span class="font-weight-medium">{{ item.plot_count || 0 }}</span>
                        </td>
                        <td
                          class="text-truncate"
                          :key="h.value + '_startDate'"
                          v-else-if="h.value === 'startDate'"
                        >
                          <div>
                            <div>{{ safeText(formatDate(item.start_date)) }}</div>
                            <!-- <div>
                              {{ safeText(formatDate(item.due_date)) }}
                              <v-tooltip v-if="isOverdue(item.due_date)" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon
                                    color="error"
                                    small
                                    v-bind="attrs"
                                    v-on="on"
                                  >mdi-alert</v-icon>
                                </template>
                                <span>Overdue</span>
                              </v-tooltip>
                            </div> -->
                          </div>
                        </td>
                        <td
                          class="text-truncate"
                          :key="h.value + '_status'"
                          v-else-if="h.value === 'status'"
                        >
                          <v-btn
                            small
                            rounded
                            min-width="200"
                            depressed
                            :class="getStatusClass(item.status)"
                            height="40"
                          >
                            {{ safeText(formatStatus(item.status)) }}
                          </v-btn>
                        </td>
                        <td :key="h.value + '_action'" v-else-if="h.value === 'action'">
                          <div style="position: absolute; right: 10px; top: 10px;">
                            <v-menu location="start">
                              <template v-slot:activator="props">
                                <v-btn icon v-bind="props.attrs" v-on="props.on">
                                  <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item @click="viewRequest(item)">
                                  <v-list-item-title>View</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="editRequest(item)">
                                  <v-list-item-title>Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="openConfirmDeleteDialog(item)">
                                  <v-list-item-title class="red--text"
                                    >Delete Request</v-list-item-title
                                  >
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </template>
                <template v-slot:footer></template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Delete Request confirmation (custom design with delete icon) -->
      <v-dialog v-model="confirmDeleteDialog" persistent max-width="500" content-class="delete-request-dialog">
        <v-card class="delete-request-dialog-card">
          <v-toolbar color="primary" elevation="0" class="white--text">
            <v-toolbar-title class="white--text font-weight-bold">Confirmation</v-toolbar-title>
            <v-spacer />
            <v-btn color="white" icon @click="confirmDeleteDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-8 pb-6 text-center">
            <div class="delete-request-icon-wrap mb-4">
              <v-icon size="48" color="orange">mdi-delete-outline</v-icon>
            </div>
            <p class="black--text mb-0">{{ deleteMessage }}</p>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn outlined color="primary" @click="confirmDeleteDialog = false">
              {{ $t('carbonCredit.plotTreeMapping.cancel') }}
            </v-btn>
            <v-btn color="primary" class="white--text ml-3" depressed @click="confirmRemoveProduct(null, confirmDeleteData)">
              DELETE
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Download scope dialog -->
      <v-dialog v-model="downloadDialog" max-width="420">
        <v-card>
          <v-toolbar color="primary" elevation="0" class="white--text">
            <v-toolbar-title class="white--text font-weight-bold">
              Download options
            </v-toolbar-title>
            <v-spacer />
            <v-btn color="white" icon @click="downloadDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-6 pb-4">
            <div class="subtitle-1 mb-2">
              Choose what to include in the download.
            </div>
            <div class="body-2">
              Current page uses the records visible in the table now.
              All records uses every record matching the current filters.
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn text color="primary" @click="downloadDialog = false">
              Cancel
            </v-btn>
            <v-btn color="primary" class="ml-2" @click="confirmDownload('page')">
              Current page
            </v-btn>
            <v-btn color="primary" class="ml-2" @click="confirmDownload('all')">
              All records
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Export data dialog -->
      <v-dialog v-model="exportDataDialog" max-width="460">
        <v-card>
          <v-toolbar color="primary" elevation="0" class="white--text">
            <v-toolbar-title class="white--text font-weight-bold">
              Export Data
            </v-toolbar-title>
            <v-spacer />
            <v-btn color="white" icon @click="closeExportDataDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-6 pb-2">
            <v-radio-group v-model="exportDateType" hide-details>
              <v-radio label="All data" value="all" />
              <v-radio label="Yesterday's data" value="yesterday" />
              <v-radio label="Choose date" value="custom" />
            </v-radio-group>
            <v-text-field
              v-if="exportDateType === 'custom'"
              v-model="exportSelectedDate"
              type="date"
              outlined
              dense
              hide-details
              class="mt-2"
              label="Select date"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn text color="primary" @click="closeExportDataDialog">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              class="ml-2"
              :loading="exportDataLoading"
              :disabled="exportDataLoading"
              @click="handleExportData"
            >
              Export
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <ImportDialog ref="plotTreeImportDialog" :after-success="() => fetchRequests()" />
      </template>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import CarbonCreditService from "@/_services/CarbonCreditService";
import ImportDialog from "@/views/carbon-credit/components/ImportDialog.vue";
import TechnicianPlotsView from "./TechnicianPlotsView.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import { decodeHtmlEntities } from "@/utils.js";

export default {
  mixins: [loadingMixin],
  components: {
    ImportDialog,
    TechnicianPlotsView,
  },
  created() {
    this.$nextTick(() => {
      this.fetchRequests();
      this.fetchAssignees();
      this.fetchProjects();
    });
  },
  async mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },
  watch: {
    options: {
      handler(opt) {
        this.fetchRequests(opt);
      },
      deep: true,
    },
    showActionsColumn() {
      this.updateDisplayHeaders();
    },
  },
  computed: {
    totalPages() {
      return parseInt(Math.ceil(this.totalRequests / this.options.limit));
    },
    displayHeaders() {
      return this.showActionsColumn
        ? this.tableHeaders
        : this.tableHeaders.filter((header) => header.value !== "action");
    },
    fromRequests() {
      return this.totalRequests > 0
        ? (this.options.page - 1) * this.options.limit + 1
        : 0;
    },
    toRequests() {
      return this.options.page * this.options.limit > this.totalRequests
        ? this.totalRequests
        : this.options.page * this.options.limit;
    },
  },
  data() {
    return {
      viewMode: 'requests',
      debounceTimer: null,
      confirmDeleteDialog: false,
      confirmDeleteData: null,
      deleteMessage: "",
      breadcrumbs: [
        { text: this.$t("dashboard"), disabled: false },
        { text: this.$t("carbonCredit.title"), disabled: false },
        { text: this.$t("carbonCredit.plotTreeMapping.title"), disabled: true },
      ],
      downloadDialog: false,
      downloadFormat: null,
      exportDataDialog: false,
      exportDateType: "all",
      exportSelectedDate: "",
      exportDataLoading: false,
      tableLoading: false,
      search: "",
      filters: {
        projectId: null,
        assignedTo: null,
        status: null,
      },
      projectFilterOptions: [],
      projectsLoading: false,
      assignedToOptions: [],
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Partially Submitted", value: "in_progress" },
        { text: "Submitted", value: "submitted" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      headers: [
        {
          text: "Request ID",
          align: "start",
          value: "request_id",
          class: "black--text",
          sortable: true,
        },
        {
          text: "Assigned To",
          align: "start",
          value: "assignedTo",
          class: "black--text",
          sortable: false,
        },
        {
          text: "Number of Plots",
          align: "start",
          value: "numberOfPlots",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.project"),
          align: "start",
          value: "project",
          class: "black--text",
          sortable: false,
        },
        {
          text: "Start Date / Due Date",
          align: "start",
          value: "startDate",
          class: "black--text",
          sortable: false,
        },
        {
          text: "Status",
          align: "center",
          value: "status",
          class: "black--text",
          sortable: true,
        },
        {
          align: "center",
          value: "action",
          class: "black--text",
          sortable: false,
        },
      ],
      allSelectedHeaders: [
        "Request ID",
        "Assigned To",
        "Number of Plots",
        this.$t("carbonCredit.plotTreeMapping.project"),
        "Start Date / Due Date",
        "Status",
      ],
      items: [],
      totalRequests: 0,
      options: {
        limit: 10,
        page: 1,
      },
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      selectedHeaders: [],
      tableHeaders: [],
      showActionsColumn: true,
      hideOnPrint: false,
      menu: false,
      selectedItem: false,
    };
  },
  methods: {
    plotTreeMappingApiContext() {
      const project = (this.projectFilterOptions || []).find(
        (p) => Number(p.id) === Number(this.filters.projectId)
      );
      return {
        selectedProjectId: this.filters.projectId,
        projectOptions: this.projectFilterOptions,
        project,
      };
    },
    safeText(val) {
      return decodeHtmlEntities(val);
    },
    async fetchRequests(opt = null) {
      this.tableLoading = true;
      const requestParams = {
        page: this.options.page || 1,
        limit: this.options.limit || 10,
        search: this.search || null,
      };

      if (this.filters.projectId) {
        requestParams.projectId = this.filters.projectId;
      }

      if (this.filters.assignedTo) {
        requestParams.assignedTo = this.filters.assignedTo;
      }

      if (this.filters.status) {
        requestParams.status = this.filters.status;
      }

      if (opt?.sortBy?.length) {
        requestParams.sortBy = opt.sortBy[0];
        requestParams.sortType = opt.sortDesc[0] ? "DESC" : "ASC";
      }

      try {
        const response = await CarbonCreditService.getPlotTreeMappingRequests(requestParams);

        if (response.success) {
          this.items = response.data.rows || [];
          this.totalRequests = response.data.total || response.data.count || 0;
        } else {
          this.$notify({
            type: "error",
            text: response.message || "Failed to fetch requests",
          });
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
        this.$notify({
          type: "error",
          text: "An error occurred while fetching requests",
        });
      } finally {
        this.tableLoading = false;
      }
    },
    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.fetchRequests();
      }, 500);
    },
    async fetchAssignees() {
      try {
        const response = await CarbonCreditService.getPlotTreeMappingAssignees();
        if (response.success) {
          this.assignedToOptions = response.data || [];
        }
      } catch (error) {
        console.error("Error fetching assignees:", error);
      }
    },
    async fetchProjects() {
      this.projectsLoading = true;
      try {
        const response = await CarbonCreditService.getPlotTreeMappingProjects({
          limit: 500,
        });
        if (response?.success !== false) {
          this.projectFilterOptions = response.data || [];
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        this.projectFilterOptions = [];
      } finally {
        this.projectsLoading = false;
      }
    },
    onFilterChange() {
      this.options.page = 1;
      this.fetchRequests();
    },
    async resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
      await this.fetchRequests();
    },
    async resetTableStructure() {
      this.tableHeaders = this.headers.filter(
        (header) =>
          (header.text && this.selectedHeaders.includes(header.text)) ||
          (!header.text)
      );
      this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
      await this.fetchRequests();
      this.menu = false;
    },
    updateDisplayHeaders() {
      this.tableHeaders = this.headers.filter(
        (header) =>
          (header.text && this.selectedHeaders.includes(header.text)) ||
          (this.showActionsColumn && header.value === "action") ||
          (!header.text && !this.showActionsColumn)
      );
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return moment(dateString).format("DD-MMM-YYYY");
    },
    formatStatus(status) {
      const statusMap = {
        pending: "Pending",
        in_progress: "Partially Submitted",
        partially_submitted: "Partially Submitted",
        submitted: "Submitted",
        completed: "Completed",
        cancelled: "Cancelled",
      };
      return statusMap[status] || status;
    },
    isOverdue(dueDate) {
      if (!dueDate) return false;
      return moment(dueDate).isBefore(moment(), "day");
    },
    getStatusClass(status) {
      switch (status) {
        case "completed":
        case "submitted":
          return "green-button";
        case "cancelled":
          return "red-button";
        case "pending":
          return "gray-button";
        case "in_progress":
        case "partially_submitted":
          return "orange-button";
        default:
          return "gray-button";
      }
    },
    selectItem(item) {
      this.selectedItem = item;
    },
    openDownloadDialog(format) {
      this.downloadFormat = format;
      this.downloadDialog = true;
    },
    openExportDataDialog() {
      this.exportDateType = "all";
      this.exportSelectedDate = "";
      this.exportDataDialog = true;
    },
    closeExportDataDialog() {
      this.exportDataDialog = false;
      this.exportDataLoading = false;
    },
    async handleExportData() {
      let dateFilter;
      if (this.exportDateType === "yesterday") {
        dateFilter = "yesterday";
      } else if (this.exportDateType === "custom") {
        if (!this.exportSelectedDate) {
          this.$notify({
            type: "error",
            text: "Please select a date to export data.",
          });
          return;
        }
        dateFilter = this.exportSelectedDate;
      }

      this.exportDataLoading = true;
      try {
        await CarbonCreditService.exportAllRequestDetails({ dateFilter });
        this.$notify({
          type: "success",
          text: "Data exported successfully.",
        });
        this.exportDataDialog = false;
      } catch (error) {
        console.error("Export data error:", error);
        this.$notify({
          type: "error",
          text: error?.data?.message || error?.data?.msg || "Failed to export data.",
        });
      } finally {
        this.exportDataLoading = false;
      }
    },
    async confirmDownload(scope) {
      this.downloadDialog = false;
      if (!this.downloadFormat) return;
      if (this.downloadFormat === 'pdf') {
        await this.exportToPDF(scope);
      } else if (this.downloadFormat === 'xlsx') {
        await this.exportToExcel(scope);
      }
    },
    pageChange(next) {
      if (next) this.options.page += 1;
      else this.options.page -= 1;
      this.fetchRequests();
    },
    addNewRequest() {
      this.$router.push({ name: "treePlotAdd" });
    },
    viewRequest(item) {
      this.$router.push({ name: "viewPlotTreeMapping", params: { id: item.id } });
    },
    editRequest(item) {
      this.$router.push({ name: "treePlotAdd", params: { id: item.id } });
    },
    openConfirmDeleteDialog(item) {
      this.confirmDeleteData = { id: item.id };
      this.deleteMessage = `Are you sure you want to delete request ${decodeHtmlEntities(item.request_id) || item.request_id}?`;
      this.confirmDeleteDialog = true;
    },
    async confirmRemoveProduct(action, confirmData) {
      if (!confirmData) return;

      try {
        this.startLoading();
        const response = await CarbonCreditService.deletePlotTreeMappingRequest(
          confirmData.id
        );

        if (response.success) {
          this.$notify({
            type: "success",
            text: "Request deleted successfully",
          });
          await this.fetchRequests();
        } else {
          this.$notify({
            type: "error",
            text: response.message || "Failed to delete request",
          });
        }
      } catch (error) {
        console.error("Error deleting request:", error);
        this.$notify({
          type: "error",
          text: "An error occurred while deleting the request",
        });
      } finally {
        this.stopLoading();
        this.confirmDeleteDialog = false;
      }
    },
    async exportToPDF(scope = 'page') {
      this.startLoading();
      try {
        const isAll = scope === 'all';
        await CarbonCreditService.exportPlotTreeMappingRequestsPdf({
          search: this.search || undefined,
          projectId: this.filters.projectId || undefined,
          status: this.filters.status || undefined,
          assignedTo: this.filters.assignedTo || undefined,
          scope,
          page: this.options.page || 1,
          limit: isAll ? this.totalRequests || 500 : this.options.limit || 10,
        });
        this.$notify({
          type: "success",
          text: scope === 'all' ? "PDF (all records) downloaded" : "PDF (current table) downloaded",
        });
      } catch (error) {
        console.error("PDF export error:", error);
        this.$notify({
          type: "error",
          text: error?.data?.message || "Failed to export PDF",
        });
      } finally {
        this.stopLoading();
      }
    },
    async exportToExcel(scope = 'page') {
      this.startLoading();
      try {
        const isAll = scope === 'all';
        await CarbonCreditService.exportPlotTreeMappingRequestsExcel({
          search: this.search || undefined,
          projectId: this.filters.projectId || undefined,
          status: this.filters.status || undefined,
          assignedTo: this.filters.assignedTo || undefined,
          scope,
          page: this.options.page || 1,
          limit: isAll ? this.totalRequests || 500 : this.options.limit || 10,
        });
        this.$notify({
          type: "success",
          text: scope === 'all' ? "Excel (all records) downloaded" : "Excel (current page) downloaded",
        });
      } catch (error) {
        console.error("Excel export error:", error);
        this.$notify({
          type: "error",
          text: error?.data?.message || "Failed to export Excel",
        });
      } finally {
        this.stopLoading();
      }
    },
    importDataFromSheet() {
      const dlg = this.$refs.plotTreeImportDialog;
      if (dlg && typeof dlg.open === "function") dlg.open();
    },
    async printPDF() {
      this.showActionsColumn = false;
      this.hideOnPrint = true;
      await this.$nextTick(); // Wait for DOM to update
      const element = document.querySelector(".v-data-table__wrapper");
      let opt = {
        margin: 1,
        filename: "PlotTreeMappingRequests.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };
      let worker = await html2pdf()
        .set(opt)
        .from(element)
        .toCanvas()
        .outputPdf("blob", "PlotTreeMappingRequests.pdf");
      let file = new File([worker], "PlotTreeMappingRequests.pdf", {
        type: "application/pdf",
      });
      var pdfUrl = await URL.createObjectURL(file);
      await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
      this.showActionsColumn = true;
      this.hideOnPrint = false;
      await this.$nextTick(); // Ensure DOM is updated after restoring
    },
  },
};
</script>

<style lang="scss" scoped>
.delete-request-dialog-card {
  background: #fff !important;
}
.delete-request-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background-color: #faf8f5;
  border-radius: 12px;
}

.green-button {
  background-color: #c1ffe7 !important;
  border: 2px solid #00bd73 !important;
  color: #489e16 !important;
}

.red-button {
  background-color: #fdecea !important;
  border: 2px solid #f03737 !important;
  color: #f03737 !important;
}

.gray-button {
  background-color: #e8e8e8 !important;
  border: 2px solid #000000 !important;
  color: #000000 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
}
</style>
